import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Navbar from '../components/Navbar.tsx';
import Footer from '../components/Footer.tsx';
import { AccountSection } from '../components/Profile/AccountSection.tsx';
import { BillingSection } from '../components/Profile/BillingSection.tsx';
import { UsageSection } from '../components/Profile/UsageSection.tsx';
import { DataPrivacySection } from '../components/Profile/DataPrivacySection.tsx';
import { DangerZone } from '../components/Profile/DangerZone.tsx';
import { useUserProfile } from '../hooks/useUserProfile.ts';
import { showToast } from '../utils/toast.ts';

export default function ProfileScreen() {
  const navigate = useNavigate();
  const profile = useUserProfile();

  console.log('[ProfileScreen] Rendering with profile:', {
    loading: profile.loading,
    hasUser: !!profile.user,
    isPro: profile.isPro,
  });

  const handleSuccessNoMessage = () => {
    // Wrapper function to match expected callback signature
    console.log('[ProfileScreen] Success callback triggered');
  };

  const handleErrorMessage = (message: string) => {
    console.log('[ProfileScreen] Error callback triggered:', message);
    showToast(message, 'error');
  };

  const handleAccountDeleted = () => {
    console.log('[ProfileScreen] Account deleted, redirecting to home');
    showToast('Account successfully deleted', 'success');
    navigate('/');
  };

  const handleGoBack = () => {
    // Navigate back to the previous page or default to upload screen
    navigate(-1);
  };

  if (profile.loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 font-sans flex flex-col">
        <Navbar />
        <main className="flex-1 container mx-auto px-6 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse space-y-6">
              <div className="h-8 bg-gray-700 rounded-md w-48"></div>
              <div className="grid gap-6">
                <div className="h-64 bg-gray-700 rounded-lg"></div>
                <div className="h-48 bg-gray-700 rounded-lg"></div>
                <div className="h-32 bg-gray-700 rounded-lg"></div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!profile.user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 font-sans flex flex-col">
        <Navbar />
        <main className="flex-1 container mx-auto px-6 py-12">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-2xl font-bold text-white mb-4">Access Denied</h1>
            <p className="text-gray-400 mb-6">Please sign in to access your profile.</p>
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Sign In
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 font-sans flex flex-col">
      <Navbar />
      <main className="flex-1 container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header with Back Button */}
          <div className="flex items-center mb-8">
            <button
              onClick={handleGoBack}
              className="flex items-center text-gray-400 hover:text-white transition-colors mr-4"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </button>
            <h1 className="text-3xl font-bold text-white">Profile Settings</h1>
          </div>

          {/* Profile Sections */}
          <div className="space-y-6">
            <AccountSection 
              profile={profile} 
            />
            
            <BillingSection 
              profile={profile} 
              onSuccess={handleSuccessNoMessage}
              onError={handleErrorMessage}
            />
            
            <UsageSection 
              profile={profile} 
            />
            
            <DataPrivacySection 
              profile={profile} 
              onSuccess={handleSuccessNoMessage}
              onError={handleErrorMessage}
            />
            
            <DangerZone 
              profile={profile} 
              onSuccess={handleAccountDeleted}
              onError={handleErrorMessage}
            />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
} 